import { IconFilter16 } from '@warp-ds/icons/react/filter-16';
import { IconSorting16 } from '@warp-ds/icons/react/sorting-16';
import { Button } from '@warp-ds/react/components/button';
import { Modal } from '@warp-ds/react/components/modal';
import { Select } from '@warp-ds/react/components/select';
import { Toggle } from '@warp-ds/react/components/toggle';
import cn from 'classnames';

import { type ChangeEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SortDetails } from 'shared/page/SortDetails';
import { thousandSeparated } from 'shared/util/formatting';
import type { ApiQueryParams, PaleState, SortValue, ThunkDispatchFunc } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';
import { actions as appStateActions } from '@/state/appStateActions';
import { actions as filterActions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';

import styles from './TopBar.module.css';

const sorting: Record<string, string> = displayValues.sorting;

type ToggleEntry = {
    label: string;
    value: SortValue;
};

export const TopBar = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();
    const [sortModalOpen, setSortModalOpen] = useState(false);

    const filtersOpen = useSelector((state: PaleState) => state.appState.filtersOpen);
    const numberOfOffers = useSelector((state: PaleState) => state.results.numberOfHotels);
    const sortOptions = useSelector((state: PaleState) => state.results.sortOptions) as SortValue[] | undefined;
    const defaultSort = useSelector((state: PaleState) => state.results.defaultSort) as SortValue;
    const selectedSort = useSelector((state: PaleState) => state.filters.sort) as SortValue | undefined;
    const currentSort = selectedSort ?? defaultSort;

    const validSortOptions = (sortOptions ?? []).filter((option) => Boolean(sorting[option as string]));

    const sortToggleOptions: ToggleEntry[] = validSortOptions.map((option) => ({
        label: sorting[option as string],
        value: option,
    }));

    const selectedSortToggle: ToggleEntry[] = sorting[currentSort as string]
        ? [{ label: sorting[currentSort as string], value: currentSort }]
        : [];

    const triggerFetch = (queryParam?: ApiQueryParams) => dispatch(fetchResults(queryParam));

    const onSortDropdownChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value as SortValue;
        dispatch(filterActions.changeSort(value === defaultSort ? undefined : value));
        triggerFetch();
    };

    const onSortToggleChange = (entry: ToggleEntry): void => {
        dispatch(filterActions.changeSort(entry.value === defaultSort ? undefined : entry.value));
        triggerFetch();
        setSortModalOpen(false);
    };

    return (
        <section
            data-testid="resultTopBar"
            className={cn(
                'fixed s-bg flex flex-row nowrap w-full justify-between items-center gap-8 py-8 px-16 text-xs md:relative md:text-m md:gap-16 md:py-8 md:px-0 md:mb-8 md:inset-auto! md:border-transparent!',
                styles.topBar,
            )}>
            <h2 className="sr-only">Resultatinnstillinger</h2>
            <Button
                primary={filtersOpen}
                utility={!filtersOpen}
                small
                onClick={() => dispatch(appStateActions.openFilter(!filtersOpen))}
                aria-label={filtersOpen ? 'Vis resultat' : 'Vis filter'}
                className="flex items-center gap-8 p-8! rounded-8 md:hidden"
                data-testid="showFilterButton">
                <IconFilter16 aria-hidden />
                {filtersOpen ? 'Resultat' : 'Filter'}
            </Button>
            <p className="text-center m-8" data-testid="resultCount">
                <span className="font-bold">{numberOfOffers ? thousandSeparated(numberOfOffers) : '0'}</span> ulike hoteller
            </p>
            <SortDetails section="pakkereiser" className="hidden md:flex" />
            <Button
                utility
                small
                onClick={() => setSortModalOpen(true)}
                className="flex items-center gap-8 p-8! rounded-8 md:hidden"
                data-testid="sortButton"
                aria-haspopup="dialog"
                aria-expanded={sortModalOpen}>
                <IconSorting16 aria-hidden />
                {sorting[currentSort as string]}
            </Button>
            <Select
                className={`hidden md:block ${styles.sortSelect}`}
                aria-label="Sorter på"
                value={currentSort as string}
                onChange={onSortDropdownChange}
                data-testid="sortDropdown">
                {validSortOptions.map((option) => (
                    <option key={option as string} value={option as string}>
                        {sorting[option as string]}
                    </option>
                ))}
            </Select>
            <Modal open={sortModalOpen} onDismiss={() => setSortModalOpen(false)} title="Sorter etter" right>
                <Toggle
                    type="radio"
                    options={sortToggleOptions}
                    selected={selectedSortToggle}
                    onChange={onSortToggleChange}
                    className="flex flex-col gap-8"
                />
            </Modal>
        </section>
    );
};
